import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { InputText } from 'primereact/inputtext';
import { Calendar, CalendarChangeParams } from 'primereact/calendar';
import exportPatientsStore from 'store/ExportPatientsStore';
import commonStore from 'store/CommonStore';
import { useEffect, useState } from 'react';
import { debounce } from 'lodash';
import { InputNumber } from 'primereact/inputnumber';
import { OptionType, PatientsFilters } from 'types';
import {
  AGE_MAX_VALUE,
  AGE_MIN_VALUE,
  DEBOUNCE_WAIT,
  GENDER,
  GLUCOMETER_SERIAL_NUMBER,
  LOGIN_MAX_LENGTH,
} from 'consts';
import { prepareDataForDateRangeFilter } from 'utils/functions';
import { format } from 'date-fns';

const isTextFilterNeedFetch = (
  filterName: string,
  value: object | string | number | null,
  minLength: number = 2,
  maxLength: number = 999,
) =>
  (filterName === 'login' || filterName === 'glucometerSerialNumber') &&
  typeof value === 'string' &&
  (value.length === 0 ||
    (value.length > minLength && value.length < maxLength));

type FilterNameType = keyof Pick<
  PatientsFilters,
  | 'gender'
  | 'diabetTypes'
  | 'weight'
  | 'glikGemoglobin'
  | 'ageMin'
  | 'ageMax'
  | 'groupNames'
  | 'login'
  | 'glucometerSerialNumber'
  | 'appId'
  | 'appVersionIds'
>;

const ExportPatientsFilters = () => {
  const [diabetTypeOptions, setDiabetTypeOptions] = useState<OptionType[]>([]);
  const [registrationDate, setRegistrationDate] = useState<
    Date | Date[] | undefined
  >(undefined);
  const [lastAuthDate, setLastAuthDate] = useState<Date | Date[] | undefined>(
    undefined,
  );

  useEffect(() => {
    commonStore.fetchDiabetTypeName().then(() => {
      setDiabetTypeOptions(commonStore.diabetTypeName);
    });
  }, []);

  const handleFilterChange = (
    value: object | string | number | null,
    filterName: FilterNameType,
  ) => {
    if (
      (filterName === 'login' && isTextFilterNeedFetch(filterName, value)) ||
      (filterName === 'glucometerSerialNumber' &&
        isTextFilterNeedFetch(filterName, value, 10, 12)) ||
      (filterName !== 'login' && filterName !== 'glucometerSerialNumber')
    ) {
      const options = { [filterName]: value };
      const filter = acceptFilter({ [filterName]: value });
      exportPatientsStore.fetchPatients({ filters: { ...options, ...filter } });
    }
  };

  const handleInputGlucoseRangeMinMax = (
    event: { value: number | null },
    field: 'glucoseMin' | 'glucoseMax',
  ) => {
    const { value } = event;
    const options = { [field]: value };
    const filter = acceptFilter({ [field]: value });
    exportPatientsStore.fetchPatients({ filters: { ...options, ...filter } });
  };

  const handleRegistrationDateChange = (e: CalendarChangeParams) => {
    const { dates } = prepareDataForDateRangeFilter(e);
    setRegistrationDate(dates);

    if (!dates) {
      const filter = acceptFilter({
        registrationDateFrom: undefined,
        registrationDateTo: undefined,
      });
      exportPatientsStore.fetchPatients({ filters: { ...filter } });
      return;
    }

    const [fromDate, toDate] = dates;
    if (toDate) {
      const filter = acceptFilter({
        registrationDateFrom: `${format(fromDate, 'yyyy-MM-dd')}T00:00:00.000Z`,
        registrationDateTo: `${format(toDate, 'yyyy-MM-dd')}T00:00:00.000Z`,
      });
      exportPatientsStore.fetchPatients({ filters: { ...filter } });
    }
  };

  const handleLastAuthDateChange = (e: CalendarChangeParams) => {
    const { dates } = prepareDataForDateRangeFilter(e);
    setLastAuthDate(dates);

    if (!dates) {
      const filter = acceptFilter({
        lastAuthorizationDateFrom: undefined,
        lastAuthorizationDateTo: undefined,
      });
      exportPatientsStore.fetchPatients({ filters: { ...filter } });
      return;
    }

    const [fromDate, toDate] = dates;
    if (toDate) {
      const filter = acceptFilter({
        lastAuthorizationDateFrom: `${format(
          fromDate,
          'yyyy-MM-dd',
        )}T00:00:00.000Z`,
        lastAuthorizationDateTo: `${format(
          toDate,
          'yyyy-MM-dd',
        )}T00:00:00.000Z`,
      });
      exportPatientsStore.fetchPatients({ filters: { ...filter } });
    }
  };

  const acceptFilter = (typeEvent: PatientsFilters) => {
    exportPatientsStore.setFilter({ ...typeEvent });
    const { filters } = exportPatientsStore;
    return filters;
  };

  const debounceLogin = debounce(
    (e) => handleFilterChange(e.target.value, 'login'),
    DEBOUNCE_WAIT,
  );
  const debounceGlucoseRange = debounce(
    (event, field) => handleInputGlucoseRangeMinMax(event, field),
    DEBOUNCE_WAIT,
  );
  const debounceWeight = debounce(
    (e) => handleFilterChange(e.value, 'weight'),
    DEBOUNCE_WAIT,
  );
  const debounceAgeMin = debounce(
    (e) => handleFilterChange(e.value, 'ageMin'),
    DEBOUNCE_WAIT,
  );
  const debounceAgeMax = debounce(
    (e) => handleFilterChange(e.value, 'ageMax'),
    DEBOUNCE_WAIT,
  );
  const debounceGlucometerSerialNumber = debounce(
    (e) => handleFilterChange(e.target.value, 'glucometerSerialNumber'),
    DEBOUNCE_WAIT,
  );

  return (
    <div className="flex flex-wrap gap-3 mb-3">
      <Dropdown
        options={GENDER}
        optionLabel="label"
        optionValue="value"
        placeholder="Пол"
        value={exportPatientsStore.filters.gender}
        onChange={(e) => handleFilterChange(e.value, 'gender')}
        showClear
      />
      <MultiSelect
        placeholder="Тип диабета"
        display="chip"
        options={diabetTypeOptions}
        value={exportPatientsStore.filters.diabetTypes}
        onChange={(e) => handleFilterChange(e.value, 'diabetTypes')}
      />
      <InputNumber
        placeholder="Вес"
        className="w-1"
        maxFractionDigits={2}
        onChange={debounceWeight}
      />
      <InputNumber
        placeholder="Минимальный возраст"
        className="w-2"
        max={AGE_MAX_VALUE}
        min={AGE_MIN_VALUE}
        onChange={debounceAgeMin}
      />
      <InputNumber
        placeholder="Максимальный возраст"
        className="w-2"
        max={AGE_MAX_VALUE}
        min={AGE_MIN_VALUE}
        onChange={debounceAgeMax}
      />
      <InputNumber
        placeholder="Целевой диапазон от"
        className="w-2"
        mode="decimal"
        minFractionDigits={1}
        maxFractionDigits={2}
        useGrouping={false}
        onChange={(e) => debounceGlucoseRange(e, 'glucoseMin')}
      />
      <InputNumber
        placeholder="Целевой диапазон до"
        className="w-2"
        mode="decimal"
        minFractionDigits={1}
        maxFractionDigits={2}
        useGrouping={false}
        onChange={(e) => debounceGlucoseRange(e, 'glucoseMax')}
      />
      <InputText
        placeholder="Логин"
        keyfilter="email"
        maxLength={LOGIN_MAX_LENGTH}
        onInput={debounceLogin}
      />
      <Calendar
        value={registrationDate}
        onChange={handleRegistrationDateChange}
        dateFormat="dd/mm/yy"
        selectionMode="range"
        placeholder="Дата регистрации"
        readOnlyInput
        showButtonBar
        showIcon
        className="w-3"
      />
      <Calendar
        value={lastAuthDate}
        onChange={handleLastAuthDateChange}
        dateFormat="dd/mm/yy"
        selectionMode="range"
        placeholder="Дата последней авторизации"
        readOnlyInput
        showButtonBar
        showIcon
        className="w-3"
      />
      <InputText
        placeholder="Серийный номер глюкометра"
        onInput={(e) => {
          (e.target as any).value = (e.target as any).value.toUpperCase();
          debounceGlucometerSerialNumber(e);
        }}
        keyfilter={/^[a-zA-Z0-9]+$/}
        maxLength={GLUCOMETER_SERIAL_NUMBER}
        className="w-3"
      />
    </div>
  );
};

export default ExportPatientsFilters;
